import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const PROJECT_KEY = "oe_project";

const DataContext = createContext(null);

export function DataProvider({ children, user, setUser }) {
  const [shoots, setShoots] = useState([]);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [projects, setProjects] = useState([]);
  const [project, setProjectState] = useState(localStorage.getItem(PROJECT_KEY) || "");
  const [loaded, setLoaded] = useState(false);
  const author = user.name;

  const reload = useCallback(async () => {
    const [s, c, t, p] = await Promise.all([api.get("/shoots"), api.get("/clients"), api.get("/team"), api.get("/projects")]);
    setShoots(s.data);
    setClients(c.data);
    setTeam(t.data);
    setProjects(p.data);
    setLoaded(true);
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const upsertShoot = useCallback((shoot) => {
    setShoots((prev) => (prev.some((s) => s.id === shoot.id) ? prev.map((s) => (s.id === shoot.id ? shoot : s)) : [...prev, shoot]));
  }, []);

  const removeShoot = useCallback((id) => setShoots((prev) => prev.filter((s) => s.id !== id)), []);

  const setProject = useCallback((id) => {
    localStorage.setItem(PROJECT_KEY, id);
    setProjectState(id);
  }, []);

  const value = useMemo(() => {
    const clientsById = Object.fromEntries(clients.map((c) => [c.id, c]));
    const projectOf = (s) => s.project_id || clientsById[s.client_id]?.project_id || "";
    const activeProject = projects.some((p) => p.id === project) ? project : "";
    return {
      shoots, clients, team, projects, loaded, reload, upsertShoot, removeShoot, author, user, setUser,
      can: (perm) => user.perms.includes(perm),
      project: activeProject, setProject, projectOf, clientsById,
      projectsById: Object.fromEntries(projects.map((p) => [p.id, p])),
      inProject: (s) => !activeProject || projectOf(s) === activeProject,
      projectClients: activeProject ? clients.filter((c) => c.project_id === activeProject || shoots.some((s) => s.client_id === c.id && projectOf(s) === activeProject)) : clients,
      teamById: Object.fromEntries(team.map((m) => [m.id, m])),
    };
  }, [shoots, clients, team, projects, project, loaded, reload, upsertShoot, removeShoot, author, user, setUser, setProject]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export const useData = () => useContext(DataContext);
